import { useState } from 'react'

import type { SobrantePorVerificar } from '@/lib/supabase/tipos-bd'
import { useSesion } from '@/modules/auth/useSesion'
import { partirDescripcion } from '@/modules/cotizador/useCotizador'
import { fechaHora } from '@/shared/utilidades/formato'

import { FotosDelSobrante } from './FotosDelSobrante'
import { cantidadLegible, leerCantidad, useVerificarSobrante } from './useSobrantes'
import estilos from './PaginaSobrantes.module.css'

type Props = { pendientes: SobrantePorVerificar[] }

/**
 * Lo que los técnicos dijeron que sobró y falta contar, por orden.
 *
 * Contar es decir cuánto llegó de verdad. Lo normal es que llegue lo que se
 * reportó, así que la casilla ya viene con eso y basta «Recibir». Si llegó
 * otra cosa, se corrige y se escribe qué pasó; si no llegó nada, «No llegó».
 */
export function PorVerificar({ pendientes }: Props) {
  if (pendientes.length === 0) {
    return (
      <p className={estilos.vacio}>
        Nada por verificar. Cuando un técnico termine una orden y diga que le sobró material,
        aparece aquí para contarlo.
      </p>
    )
  }

  const porOrden = new Map<string, SobrantePorVerificar[]>()
  for (const p of pendientes) {
    porOrden.set(p.orden_trabajo_id, [...(porOrden.get(p.orden_trabajo_id) ?? []), p])
  }

  return (
    <div className={estilos.ordenes}>
      {[...porOrden.values()].map((items) => (
        <OrdenPorVerificar key={items[0].orden_trabajo_id} items={items} />
      ))}
    </div>
  )
}

function OrdenPorVerificar({ items }: { items: SobrantePorVerificar[] }) {
  const { puede } = useSesion()
  const verificar = useVerificarSobrante()
  const [error, setError] = useState<string | null>(null)
  const puedeVerificar = puede('sobrantes', 'aprobar')
  const o = items[0]

  async function recibirTodo() {
    setError(null)
    try {
      for (const it of items) {
        await verificar.mutateAsync({ sobranteId: it.id, cantidad: it.cantidad_reportada })
      }
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo recibir.')
    }
  }

  return (
    <section className={estilos.orden}>
      <header className={estilos.ordenCabecera}>
        <div>
          <p className={estilos.ordenNumero}>
            {o.orden_trabajo}
            {o.orden_servicio && <span> · {o.orden_servicio}</span>}
          </p>
          <p className={estilos.ordenDatos}>
            {o.cliente ?? 'Sin cliente'} · reportó {o.tecnico ?? 'la oficina'} ·{' '}
            {fechaHora(o.reportado_en)}
          </p>
        </div>
        {puedeVerificar && items.length > 1 && (
          <button
            type="button"
            className={estilos.botonSuave}
            disabled={verificar.isPending}
            onClick={() => void recibirTodo()}
          >
            Llegó todo como se reportó
          </button>
        )}
      </header>

      <div className={estilos.ordenFotos}>
        <FotosDelSobrante rutas={o.fotos ?? []} />
      </div>

      <ul className={estilos.renglones}>
        {items.map((it) => (
          <RenglonPorVerificar key={it.id} item={it} puedeVerificar={puedeVerificar} />
        ))}
      </ul>
      {error && <p className={estilos.error}>{error}</p>}
    </section>
  )
}

function RenglonPorVerificar({
  item,
  puedeVerificar,
}: {
  item: SobrantePorVerificar
  puedeVerificar: boolean
}) {
  const verificar = useVerificarSobrante()
  const [texto, setTexto] = useState(cantidadLegible(item.cantidad_reportada).replace('.', ''))
  const [nota, setNota] = useState('')
  const [error, setError] = useState<string | null>(null)
  const { codigo, nombre } = partirDescripcion(item.descripcion)

  const llego = leerCantidad(texto)
  const distinto = llego !== null && llego !== item.cantidad_reportada
  const faltaNota = distinto && nota.trim().length < 4
  const noLlego = llego === 0

  async function guardar(cantidad: number) {
    setError(null)
    try {
      await verificar.mutateAsync({ sobranteId: item.id, cantidad, nota: nota.trim() || null })
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo guardar.')
    }
  }

  return (
    <li className={estilos.renglon}>
      <div className={estilos.renglonMaterial}>
        <span className={estilos.nombre}>{nombre}</span>
        <span className={estilos.detalle}>
          {codigo && <span className={estilos.codigo}>{codigo}</span>}
          Reportó <strong>{cantidadLegible(item.cantidad_reportada)}</strong> {item.unidad ?? ''}
        </span>
      </div>

      {puedeVerificar ? (
        <div className={estilos.renglonAcciones}>
          <label className={estilos.llego}>
            <span>Llegó</span>
            <input
              type="text"
              inputMode="decimal"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              aria-label={`Cuánto llegó de ${nombre}`}
            />
            <span className={estilos.unidad}>{item.unidad ?? ''}</span>
          </label>
          <button
            type="button"
            className={estilos.botonFuerte}
            disabled={verificar.isPending || llego === null || llego < 0 || faltaNota}
            onClick={() => llego !== null && void guardar(llego)}
          >
            {noLlego ? 'Confirmar' : 'Recibir'}
          </button>
          {!noLlego && (
            <button type="button" className={estilos.botonTexto} onClick={() => setTexto('0')}>
              No llegó
            </button>
          )}
        </div>
      ) : (
        <span className={estilos.etiqueta}>Por verificar</span>
      )}

      {/* La nota solo cuando llegó distinto: es lo único que hay que explicar. */}
      {puedeVerificar && distinto && (
        <input
          className={`${estilos.nota} ${faltaNota ? estilos.notaFalta : ''}`}
          type="text"
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          placeholder={
            noLlego
              ? 'No llegó: ¿qué pasó? (obligatorio)'
              : `Llegó distinto de ${cantidadLegible(item.cantidad_reportada)}: ¿qué pasó? (obligatorio)`
          }
          aria-label="Nota de la verificación"
        />
      )}
      {error && <p className={estilos.error}>{error}</p>}
    </li>
  )
}
